<template>
  <div>
    <HeaderBar>
      <v-icon slot="icon">device_hub</v-icon>
      Manager Users
      <template slot="buttons">
        <router-link :to="{ name: 'hrManagers' }">
          Back to Managers
        </router-link>
      </template>
    </HeaderBar>

    <v-layout class="my-2" v-if="gettingManager">
      <v-flex>
        <v-progress-linear indeterminate></v-progress-linear>
      </v-flex>
    </v-layout>

    <v-layout v-else>
      <v-layout class="my-2">
        <v-row>
          <v-flex class="my-4">
            <h1 class="display-1 mb-2">{{ manager.name }}</h1>
            <h1 v-if="!alreadyDeactivated" class="subheading mb-2">
              {{ manager.email }}
            </h1>
            <p v-if="alreadyDeactivated" class="subtle">
              This user has been deactivated and can no longer log in
            </p>
          </v-flex>

          <v-layout row wrap class="mb-2">
            <v-flex class="mr-2">
              <ApiField
                :hint="
                  !manager.first_name
                    ? 'Some names did not import properly, please update this field'
                    : ''
                "
                label="First Name"
                storeAction="managers/update"
                storeUpdate="managers/getManager"
                paramName="manager"
                :object="manager"
                fieldName="first_name"
                :storeFieldValue="manager.first_name"
                @updateManager="updateManager"
              >
              </ApiField>
            </v-flex>
            <v-flex>
              <ApiField
                :hint="
                  !manager.last_name
                    ? 'Some names did not import properly, please update this field'
                    : ''
                "
                label="Last Name"
                storeAction="managers/update"
                storeUpdate="managers/getManager"
                paramName="manager"
                :object="manager"
                fieldName="last_name"
                :storeFieldValue="manager.last_name"
                @updateManager="updateManager"
              >
              </ApiField>
            </v-flex>
          </v-layout>
          <v-layout row wrap class="mb-2">
            <v-flex>
              <ApiField
                :disabled="!updatingEmail"
                hint="This is the user's login. Only change this if you are sure it's correct."
                label="Email / Login"
                storeAction="managers/update"
                storeUpdate="managers/getManager"
                paramName="manager"
                :object="manager"
                fieldName="email"
                :storeFieldValue="manager.email"
              >
              </ApiField>
            </v-flex>

            <v-flex>
              <v-btn
                v-if="!updatingEmail"
                color="warning"
                @click="updatingEmail = !updatingEmail"
                >Change Email</v-btn
              >
              <v-btn
                v-if="updatingEmail"
                color="warning"
                @click="updatingEmail = !updatingEmail"
                >Lock Email</v-btn
              >
            </v-flex>
          </v-layout>

          <v-layout row wrap class="mb-2">
            <v-flex>
              <ApiField
                label="Phone"
                storeAction="managers/update"
                storeUpdate="managers/getManager"
                paramName="manager"
                :object="manager"
                fieldName="phone"
                :storeFieldValue="manager.phone"
                @updateManager="updateManager"
              >
              </ApiField>
            </v-flex>
          </v-layout>

          <v-row>
            <ApiField
              fieldType="switch"
              label="Dashboard shows all Locations"
              storeAction="managers/update"
              paramName="manager"
              :object="manager"
              fieldName="can_dashboard_all_locations"
              :storeFieldValue="manager.canDashboardAllLocations"
            />
          </v-row>

          <v-flex v-if="!alreadyDeactivated">
            <PasswordResetDialog :email="manager.email" />
          </v-flex>
        </v-row>

        <v-flex class="text-xs-right" v-if="alreadyDeactivated">
          <v-btn @click="confirmActivate = true">Activate</v-btn>
          <v-dialog v-model="confirmActivate" max-width="300">
            <v-card>
              <v-card-title class="headline">
                Are you sure?
              </v-card-title>
              <v-card-text>
                User can log in again after you send them password reset
                instructions.
              </v-card-text>
              <v-card-actions>
                <v-spacer />
                <v-btn flat color="grey" @click="confirmActivate = false"
                  >Nevermind</v-btn
                >
                <v-btn
                  @click="activateManager"
                  color="success"
                  :disabled="activatePending"
                  :loading="activatePending"
                  >Yes, activate</v-btn
                >
              </v-card-actions>
            </v-card>
          </v-dialog>
        </v-flex>
        <v-flex class="text-xs-right" v-else>
          <v-btn color="error" @click="confirmDeactivate = true"
            >Deactivate</v-btn
          >
          <v-dialog v-model="confirmDeactivate" max-width="300">
            <v-card>
              <v-card-title class="headline">
                Are you sure?
              </v-card-title>
              <v-card-text>
                This will make it impossible for this user to log in, but the
                account will not be deleted. Any locations or employees assigned
                will be unassociated.
              </v-card-text>
              <v-card-actions>
                <v-spacer />
                <v-btn flat color="grey" @click="confirmDeactivate = false"
                  >Nevermind</v-btn
                >
                <v-btn
                  @click="deactivateManager"
                  color="success"
                  :disabled="deactivatePending"
                  :loading="deactivatePending"
                  >Yes, deactivate</v-btn
                >
              </v-card-actions>
            </v-card>
          </v-dialog>
        </v-flex>
      </v-layout>
    </v-layout>
  </div>
</template>

<script>
import { mapState } from "vuex";
import HeaderBar from "@/components/base/header-bar.vue";
import PasswordResetDialog from "@/components/hr/password-reset-dialog.vue";
import managerApi from "@/api/manager";
import ApiField from "@/components/base/api-field.vue";

export default {
  components: {
    HeaderBar,
    PasswordResetDialog,
    ApiField
  },
  computed: {
    ...mapState("userAuth", ["user"]),
    alreadyDeactivated() {
      return this.manager.deactivated;
    }
  },
  created() {
    this.setUp();
  },
  watch: {},
  data: function() {
    return {
      manager: null,
      gettingManager: false,
      confirmDeactivate: false,
      deactivatePending: false,
      confirmActivate: false,
      activatePending: false,
      updatingEmail: false
    };
  },
  methods: {
    setUp() {
      this.gettingManager = true;
      this.$store
        .dispatch("managers/getManager", this.$route.params.managerId)
        .then(manager => {
          this.manager = manager;
        })
        .finally(res => {
          this.gettingManager = false;
        });
    },

    deactivateManager() {
      managerApi
        .deactivate(this.manager.id)
        .then(r => {
          this.$store.dispatch("global/showSnackbarSuccess", {
            message: `${this.manager.name} deactivated!`
          });
        })
        .catch(e => {
          this.$store.dispatch("global/showSnackbarError", {
            message: `Something went wrong deactivating ${this.manager.name}...`
          });
        })
        .finally(r => {
          this.confirmDeactivate = false;
          this.deactivatePending = false;
          this.manager.deactivated = true;
        });
    },
    activateManager() {
      managerApi
        .activate(this.manager.id)
        .then(r => {
          this.$store.dispatch("global/showSnackbarSuccess", {
            message: `${this.manager.name} activated!`
          });
        })
        .catch(e => {
          this.$store.dispatch("global/showSnackbarError", {
            message: `Something went wrong deactivating ${this.manager.name}...`
          });
        })
        .finally(r => {
          this.confirmActivate = false;
          this.activatePending = false;
          this.manager.deactivated = false;
        });
    },
    updateManager(updatedManager) {
      this.manager = updatedManager;
    }
  }
};
</script>

<style scoped></style>
